import { describeRpcError, isAuthorizationError } from "@how/app-kit";
import {
  ActionIcon,
  Badge,
  Button,
  Card,
  Checkbox,
  Group,
  Loader,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconTrash } from "@tabler/icons-react";
import { zod4Resolver } from "mantine-form-zod-resolver";
import { useRef, useState } from "react";
import { useChurchCampuses, useSetMyCampus } from "./campus-queries";
import {
  ConfirmActionModal,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  PermissionDenied,
  useFocusWhenReady,
  DISPLAY_LOCALE,
} from "@how/ui";
import { z } from "zod";
import {
  CHANNELS,
  useMyProfile,
  useRemoveMyContact,
  useSetMyContact,
  useSetMyPreference,
  useUpdateMyDetails,
  type MyContact,
  type MyHouseholdMember,
  type MyPreference,
  type MyProfile,
} from "./profile-queries";
import { semanticColor } from "@how/design-system";

function notifyError(title: string, error: unknown): void {
  const described = describeRpcError(error);
  notifications.show({
    color: described.kind === "denied" ? "yellow" : "red",
    title,
    message: described.message,
  });
}

/**
 * A member's own record (ADR 0045). Everything here is resolved from the login on the server; the screen never
 * sends a person id. Three states before the record: loading, no portal access, and a login the church has not
 * linked to a person yet — the last is the common one on a new church and is explained, not retried.
 */
export function ProfilePage() {
  const profile = useMyProfile();
  return (
    <>
      <PageHeader
        title="My profile"
        description="Your details as your church holds them. What you change here, the church sees at once."
      />
      {profile.isPending ? (
        <LoadingState rows={6} />
      ) : profile.isError ? (
        isAuthorizationError(profile.error) ? (
          <PermissionDenied />
        ) : describeRpcError(profile.error).kind === "missing" ? (
          <EmptyState
            title="Your church has not linked your login to a record yet"
            description="Ask your church office to connect your sign-in to your record. Until then there is nothing to show here."
          />
        ) : (
          <ErrorState
            message="Could not load your profile."
            onRetry={() => {
              void profile.refetch();
            }}
          />
        )
      ) : (
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
          <Stack gap="lg">
            <DetailsCard profile={profile.data} />
            <CampusCard profile={profile.data} />
            <ContactsCard contacts={profile.data.contacts} />
          </Stack>
          <Stack gap="lg">
            <PreferencesCard preferences={profile.data.preferences} />
            <HouseholdCard household={profile.data.household} />
          </Stack>
        </SimpleGrid>
      )}
    </>
  );
}

const detailsSchema = z.object({
  firstName: z.string().trim().min(1, "Your first name is required"),
  lastName: z.string().trim().min(1, "Your last name is required"),
  preferredName: z.string().trim(),
});

const NO_CAMPUS = "__none__";

/**
 * My campus (ADR 0036 amendment, the member's half): the member picks, changes or clears where they usually worship.
 * Groups and events at that campus come first in the portal; the church's record follows. Shown only when the church
 * has more than one campus: a single-building church never thinks about it.
 */
function CampusCard({ profile }: { profile: MyProfile }) {
  const campuses = useChurchCampuses();
  const setCampus = useSetMyCampus();
  const headingRef = useRef<HTMLHeadingElement>(null);
  const current = profile.person.campus?.id ?? NO_CAMPUS;
  const [chosen, setChosen] = useState<string>(current);
  const list = campuses.data ?? [];
  if (campuses.isPending || list.length < 2) return null;
  const options = [
    ...list.map((c) => ({ value: c.id, label: c.name })),
    { value: NO_CAMPUS, label: "No home campus" },
  ];
  const changed = chosen !== current;
  return (
    <Card>
      <Stack gap="sm">
        <div>
          <Title order={2} size="h4" ref={headingRef} tabIndex={-1}>
            My campus
          </Title>
          <Text size="sm" c="dimmed">
            Where you usually worship. Groups and events at your campus come first; everything open
            to all campuses still shows.
          </Text>
        </div>
        <Select
          label="Home campus"
          data={options}
          value={chosen}
          onChange={(value) => {
            setChosen(value ?? NO_CAMPUS);
          }}
          allowDeselect={false}
          maw={360}
        />
        <Group justify="flex-end">
          <Button
            loading={setCampus.isPending}
            disabled={!changed}
            onClick={() => {
              const next = chosen === NO_CAMPUS ? null : chosen;
              const name = list.find((c) => c.id === next)?.name ?? null;
              setCampus.mutate(next, {
                onSuccess: () => {
                  headingRef.current?.focus();
                  notifications.show({
                    color: semanticColor("success"),
                    title: name === null ? "No home campus" : `${name} is your campus`,
                    message:
                      name === null
                        ? "Groups and events from every campus show."
                        : `Groups and events at ${name} come first.`,
                  });
                },
                onError: (error) => {
                  notifications.show({
                    color: semanticColor("danger"),
                    title: "Could not save your campus",
                    message: describeRpcError(error).message,
                  });
                },
              });
            }}
          >
            Save campus
          </Button>
        </Group>
      </Stack>
    </Card>
  );
}

function DetailsCard({ profile }: { profile: MyProfile }) {
  const update = useUpdateMyDetails();
  // Save is disabled while saving and again once saved (nothing changed since): focus is put somewhere (#285).
  const headingRef = useRef<HTMLHeadingElement>(null);
  const saveRef = useRef<HTMLButtonElement>(null);
  const form = useForm({
    initialValues: {
      firstName: profile.person.firstName,
      lastName: profile.person.lastName,
      preferredName: profile.person.preferredName ?? "",
    },
    validate: zod4Resolver(detailsSchema),
  });
  const birthdate =
    profile.person.birthdate === null
      ? null
      : new Date(`${profile.person.birthdate}T00:00:00`).toLocaleDateString(DISPLAY_LOCALE);
  return (
    <Card>
      <form
        onSubmit={form.onSubmit((values) => {
          update.mutate(values, {
            onSuccess: () => {
              form.resetDirty(values);
              headingRef.current?.focus();
              notifications.show({
                color: semanticColor("success"),
                title: "Saved",
                message: "Your details are updated.",
              });
            },
            onError: (error) => {
              saveRef.current?.focus();
              notifyError("Could not save your details", error);
            },
          });
        })}
      >
        <Stack gap="sm">
          <Title order={2} size="h3" ref={headingRef} tabIndex={-1}>
            Your details
          </Title>
          <TextInput label="First name" required {...form.getInputProps("firstName")} />
          <TextInput label="Last name" required {...form.getInputProps("lastName")} />
          <TextInput
            label="Preferred name"
            description="What people call you, if it differs."
            {...form.getInputProps("preferredName")}
          />
          <Group gap="xl">
            <Stack gap={2}>
              <Text size="xs" c="dimmed">
                Birthdate
              </Text>
              <Text size="sm">{birthdate ?? "Not on record"}</Text>
            </Stack>
          </Group>
          <Text size="xs" c="dimmed">
            To correct your birthdate, ask your church office.
          </Text>
          <Group justify="flex-end">
            <Button
              ref={saveRef}
              type="submit"
              loading={update.isPending}
              disabled={!form.isDirty()}
            >
              Save details
            </Button>
          </Group>
        </Stack>
      </form>
    </Card>
  );
}

const contactSchema = z.object({
  method: z.enum(["email", "phone"]),
  value: z.string().trim().min(1, "Enter an email address or phone number"),
  label: z.string().trim(),
  isPrimary: z.boolean(),
});

type ContactFields = z.infer<typeof contactSchema>;

function ContactsCard({ contacts }: { contacts: readonly MyContact[] }) {
  const setContact = useSetMyContact();
  const removeContact = useRemoveMyContact();
  const [removing, setRemoving] = useState<MyContact | null>(null);
  const focusWhenReady = useFocusWhenReady();
  const saveContactRef = useRef<HTMLButtonElement>(null);
  const form = useForm<ContactFields>({
    initialValues: { method: "email", value: "", label: "", isPrimary: false },
    validate: zod4Resolver(contactSchema),
  });
  return (
    <Card>
      <Stack gap="sm">
        <Title order={2} size="h3">
          How to reach you
        </Title>
        {contacts.length === 0 ? (
          <Text size="sm" c="dimmed">
            Your church has no email address or phone number for you.
          </Text>
        ) : (
          <Stack gap="xs">
            {contacts.map((contact) => (
              <Group
                key={contact.id}
                justify="space-between"
                wrap="nowrap"
                data-contact={contact.id}
              >
                <Stack gap={0} style={{ minWidth: 0 }}>
                  <Group gap="xs">
                    <Text size="sm" style={{ overflowWrap: "anywhere" }}>
                      {contact.value}
                    </Text>
                    {contact.isPrimary && (
                      <Badge size="xs" variant="light">
                        Primary
                      </Badge>
                    )}
                  </Group>
                  <Text size="xs" c="dimmed">
                    {contact.method === "email" ? "Email" : "Phone"}
                    {contact.label ? ` · ${contact.label}` : ""}
                  </Text>
                </Stack>
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  aria-label={`Remove ${contact.value}`}
                  onClick={() => {
                    setRemoving(contact);
                  }}
                >
                  <IconTrash size={16} />
                </ActionIcon>
              </Group>
            ))}
          </Stack>
        )}
        <form
          onSubmit={form.onSubmit((values) => {
            setContact.mutate(values, {
              onSuccess: () => {
                form.reset();
                // Save was disabled while saving; focus goes back to the address, ready for another (#285).
                focusWhenReady("[data-contact-value]");
                notifications.show({
                  color: semanticColor("success"),
                  title: "Saved",
                  message: "Your contact details are updated.",
                });
              },
              onError: (error) => {
                saveContactRef.current?.focus();
                notifyError("Could not save that contact method", error);
              },
            });
          })}
        >
          <Stack gap="xs">
            <Text size="sm" fw={500}>
              Add or update one
            </Text>
            <Group grow align="flex-start">
              <Select
                label="Type"
                data={[
                  { value: "email", label: "Email" },
                  { value: "phone", label: "Phone" },
                ]}
                allowDeselect={false}
                {...form.getInputProps("method")}
              />
              <TextInput
                label={form.values.method === "email" ? "Email address" : "Phone number"}
                data-contact-value
                required
                {...form.getInputProps("value")}
              />
            </Group>
            <Group grow align="flex-end">
              <TextInput
                label="Label"
                placeholder="personal, work, mobile"
                {...form.getInputProps("label")}
              />
              <Checkbox
                label="Use this first"
                {...form.getInputProps("isPrimary", { type: "checkbox" })}
              />
            </Group>
            <Group justify="flex-end">
              <Button
                ref={saveContactRef}
                type="submit"
                variant="light"
                loading={setContact.isPending}
              >
                Save contact
              </Button>
            </Group>
          </Stack>
        </form>
      </Stack>
      <ConfirmActionModal
        opened={removing !== null}
        onClose={() => {
          setRemoving(null);
        }}
        title="Remove this contact method?"
        message={`${removing?.value ?? ""} will be removed from your record. Your church will no longer reach you there.`}
        confirmLabel="Remove"
        danger
        loading={removeContact.isPending}
        onConfirm={() => {
          if (removing === null) return;
          const index = contacts.findIndex((c) => c.id === removing.id);
          const next = contacts[index + 1] ?? contacts[index - 1];
          removeContact.mutate(removing.id, {
            onSuccess: () => {
              // The contact leaves the list: focus goes to the next one's Remove, or to the form to add one (#285).
              focusWhenReady(
                next === undefined ? "[data-contact-value]" : `[data-contact="${next.id}"] button`,
                { gone: `[data-contact="${removing.id}"]` },
              );
              setRemoving(null);
              notifications.show({
                color: semanticColor("success"),
                title: "Removed",
                message: `${removing.value} is removed.`,
              });
            },
            onError: (error) => {
              notifyError("Could not remove that contact method", error);
            },
          });
        }}
      />
    </Card>
  );
}

/**
 * The member half of consent. Three states, the same as the church sees: never asked, said yes, said no. The
 * server records "the member, in their portal" as the source, so a choice made here outranks anything a form said.
 */
function PreferencesCard({ preferences }: { preferences: readonly MyPreference[] }) {
  const setPreference = useSetMyPreference();
  return (
    <Card>
      <Stack gap="sm">
        <Title order={2} size="h3">
          What you agree to receive
        </Title>
        <Text size="sm" c="dimmed">
          Your choice here is recorded in your name and applies at once.
        </Text>
        {CHANNELS.map((channel) => {
          const recorded = preferences.find((p) => p.channel === channel.value);
          const pending =
            setPreference.isPending && setPreference.variables.channel === channel.value;
          return (
            // Busy while saving, with a spinner in the button pressed, rather than Mantine's loading: that disabled the
            // button and dropped focus. A press while saving is ignored, as the disabled button used to ensure.
            <Group
              key={channel.value}
              justify="space-between"
              wrap="nowrap"
              align="flex-start"
              aria-busy={pending}
            >
              <Stack gap={0}>
                <Text size="sm">{channel.label}</Text>
                <Text size="xs" c="dimmed">
                  {recorded === undefined
                    ? "You have not said"
                    : `${recorded.optedIn ? "Yes" : "No"}, ${new Date(recorded.recordedAt).toLocaleDateString(DISPLAY_LOCALE)} · ${recorded.source}`}
                </Text>
              </Stack>
              <Group gap="xs" wrap="nowrap">
                <Button
                  size="compact-sm"
                  variant={recorded?.optedIn === true ? "light" : "subtle"}
                  color={semanticColor("success")}
                  aria-label={`Yes to ${channel.label}`}
                  aria-pressed={recorded?.optedIn === true}
                  leftSection={
                    pending && setPreference.variables.optedIn ? (
                      <Loader size={12} color="currentColor" />
                    ) : undefined
                  }
                  onClick={() => {
                    if (recorded?.optedIn === true || setPreference.isPending) return;
                    setPreference.mutate(
                      { channel: channel.value, optedIn: true },
                      {
                        onError: (error) => {
                          notifyError("Could not record your choice", error);
                        },
                      },
                    );
                  }}
                >
                  Yes
                </Button>
                <Button
                  size="compact-sm"
                  variant={recorded?.optedIn === false ? "light" : "subtle"}
                  color="gray"
                  aria-label={`No to ${channel.label}`}
                  aria-pressed={recorded?.optedIn === false}
                  leftSection={
                    pending && !setPreference.variables.optedIn ? (
                      <Loader size={12} color="currentColor" />
                    ) : undefined
                  }
                  onClick={() => {
                    if (recorded?.optedIn === false || setPreference.isPending) return;
                    setPreference.mutate(
                      { channel: channel.value, optedIn: false },
                      {
                        onError: (error) => {
                          notifyError("Could not record your choice", error);
                        },
                      },
                    );
                  }}
                >
                  No
                </Button>
              </Group>
            </Group>
          );
        })}
      </Stack>
    </Card>
  );
}

function memberName(m: MyHouseholdMember): string {
  return `${m.preferredName ?? m.firstName} ${m.lastName}`;
}

const ROLE_LABEL = { adult: "Adult", child: "Child", other: "Other" } as const;

/** Read-only: who shares the member's household. Children stay with the church until counsel answers (ADR 0028). */
function HouseholdCard({ household }: { household: MyProfile["household"] }) {
  const address =
    household === null
      ? []
      : [
          household.address.line1,
          household.address.line2,
          [household.address.locality, household.address.region, household.address.postalCode]
            .filter((part) => part !== undefined && part !== "")
            .join(" "),
        ].filter((line): line is string => line !== undefined && line !== "");
  return (
    <Card>
      <Stack gap="sm">
        <Title order={2} size="h3">
          Your household
        </Title>
        {household === null ? (
          <Text size="sm" c="dimmed">
            Your church has not placed you in a household.
          </Text>
        ) : (
          <>
            <Stack gap={0}>
              <Text size="sm" fw={500}>
                {household.name}
              </Text>
              {address.map((line) => (
                <Text key={line} size="sm" c="dimmed">
                  {line}
                </Text>
              ))}
            </Stack>
            <Stack gap="xs">
              {household.members.map((member) => (
                <Group key={member.personId} justify="space-between" wrap="nowrap">
                  <Text size="sm">
                    {memberName(member)}
                    {member.isMe ? " (you)" : ""}
                  </Text>
                  <Group gap="xs">
                    {member.isPrimaryContact && (
                      <Badge size="xs" variant="light">
                        Primary contact
                      </Badge>
                    )}
                    <Badge size="xs" variant="outline" color="gray">
                      {ROLE_LABEL[member.role]}
                    </Badge>
                  </Group>
                </Group>
              ))}
            </Stack>
            <Text size="xs" c="dimmed">
              To change your household or its address, or anything about a child, ask your church
              office.
            </Text>
          </>
        )}
      </Stack>
    </Card>
  );
}
